import React from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

export interface FeatureToggleChipProps {
  /**
   * Label to display
   */
  label: string;

  /**
   * Icon to display (left side)
   */
  icon: React.ReactNode;

  /**
   * Click handler to toggle/remove the feature
   */
  onClick: () => void;

  /**
   * Additional className
   */
  className?: string;

  /**
   * Whether to show hover effect with X icon
   * @default true
   */
  showHoverRemove?: boolean;

  /**
   * Custom colors
   */
  colors?: {
    bg?: string;
    bgHover?: string;
    text?: string;
  };
}

/**
 * Chip component for toggleable features (Video Mode, OCR, etc.)
 *
 * Shows an icon and label, with an X icon on hover for removal
 */
export const FeatureToggleChip: React.FC<FeatureToggleChipProps> = ({
  label,
  icon,
  onClick,
  className = '',
  showHoverRemove = true,
  colors,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex items-center gap-2 h-7 rounded-full px-3 transition-all',
        'animate-in slide-in-from-left-2 fade-in duration-300',
        colors?.bg || 'bg-primary/10',
        colors?.bgHover || 'hover:bg-primary/15',
        colors?.text || 'text-primary',
        className,
      )}
    >
      {showHoverRemove ? (
        <div className="relative w-4 h-4">
          <div className="absolute inset-0 transition-opacity group-hover:opacity-0">
            {icon}
          </div>
          <div className="absolute inset-0 transition-opacity opacity-0 group-hover:opacity-100">
            <X className="h-4 w-4" strokeWidth={2} />
          </div>
        </div>
      ) : (
        <div className="w-4 h-4">{icon}</div>
      )}
      <span className="text-xs font-medium">{label}</span>
    </button>
  );
};
